import React, { useEffect, useState } from "react";
import useResponsive from "../../hooks/useResponsive";
import { useNotificationService } from "../notification";
import { useTheme } from "@mui/material/styles";
import { useForm } from "react-hook-form";
import { otpcodeSchema } from "../../validate/authentication";
import { useMutation } from "@tanstack/react-query";
import { Box, Stack, Typography } from "@mui/material";
import { OTPInput } from "../UIElements/inputs";
import { LoadingButton } from "@mui/lab";
import { zodResolver } from "@hookform/resolvers/zod";
import { apiSignIn } from "../../api/auth";
import { useNavigate } from "react-router-dom";
import { PATH_DASHBOARD } from "../../routes/path";
import { useAuth } from "../../hooks/useAuth";

interface CodeProps {
  openOtp: boolean;
  data: { email: string; password: string };
  setOpenOtp: React.Dispatch<React.SetStateAction<boolean>>;
}

const OptCodeDetails = ({ openOtp, setOpenOtp, data }: CodeProps) => {
  const { showNotification } = useNotificationService();
  const theme = useTheme();
  const navigate = useNavigate();
  const { set_cookie_token, set_data } = useAuth();
  const [countdown, setCountdown] = useState(120); // 120 seconds = 2 minutes
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  // set up validation
  const {
    register,
    formState: { errors, isSubmitSuccessful },
    handleSubmit,
  } = useForm({
    resolver: zodResolver(otpcodeSchema),
  });

  // set up react query mutation
  const mutation = useMutation({
    mutationFn: apiSignIn,
    onSuccess: (data) => {
      // Handle error
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
      }
      //  Handle success
      if ("data" in data) {
        showNotification(data?.data?.message ?? "", "success");
        const userData = {
          _id: data?.data?.data?._id ?? "",
          role: data?.data?.data?.role ?? "",
          first_name: data?.data?.data?.first_name ?? "",
          last_name: data?.data?.data?.last_name ?? "",
          middle_name: data?.data?.data?.middle_name ?? "",
          name: data?.data?.data?.name ?? "",
          email: data?.data?.data?.email ?? "",
          gender: data?.data?.data?.gender ?? "",
          phone_number: data?.data?.data?.phone_number ?? "",
          isVerified: data?.data?.data?.isVerified ?? false,
        };
        set_cookie_token(data?.data?.data);
        set_data(userData);

        navigate(PATH_DASHBOARD.root);
      } else {
        showNotification("Verification failed", "error");
      }
    },
    onError: (error: any) => {
      // Handle error
      showNotification("Verification failed", "error");
    },
  });

  // set up resend email notification
  const resendEmail = useMutation({
    mutationFn: apiSignIn,
    onSuccess: (data) => {
      //  Handle success
      if ("data" in data) {
        showNotification(data?.data?.message ?? "", "success");
      }
    },
    onError: (error) => {
      console.error("Email verification failed", error);
      // Handle error
      showNotification("Email verification failed", "error");
    },
  });

  const handleOnSubmit = async (value: any) => {
    const { code } = value;
    const stringNumber = code;
    const current_code = stringNumber.replace(/-/g, "");
    mutation.mutate({
      email: data.email,
      password: data.password,
      otp: current_code,
    });
  };

  const handleResend = async () => {
    if (canResend) {
      // Your resend logic here
      resendEmail.mutate({
        email: data.email,
        password: data.password,
        otp: "",
      });
      setCountdown(120); // Reset countdown
      setCanResend(false);
    }
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit(handleOnSubmit)}
      >
        <OTPInput
          labelText="OTP"
          placeholder="0 - 0 - 0 - 0 - 0 - 0"
          errors={errors}
          register={register}
        />

        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"end"}
          sx={{ my: "1.5rem" }}
        >
          <LoadingButton
            loading={mutation.isPending}
            variant="contained"
            type="submit"
            sx={{
              marginLeft: "auto",
              width: "100%",
              borderRadius: "12px",
              fontWeight: 700,
            }}
          >
            Continue
          </LoadingButton>
        </Stack>
        <Stack direction={"row"} justifyContent={"space-between"}>
          <Typography
            onClick={handleResend}
            sx={{
              fontSize: "16px",
              lineHeight: "27px",
              color: canResend
                ? theme.palette.primary.main
                : theme.palette.text.disabled,
              cursor: canResend ? "pointer" : "default",
              "&:hover": {
                textDecoration: canResend ? "underline" : "none",
              },
            }}
          >
            {canResend ? "Resend OTP" : `Resend OTP (${formatTime(countdown)})`}
          </Typography>
          <Typography
            onClick={() => setOpenOtp(true)}
            sx={{
              fontSize: "16px",
              lineHeight: "27px",
              cursor: "pointer",
              color: theme.palette.text.disabled,
            }}
          >
            Back
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

export default OptCodeDetails;
